import React from 'react';
import { motion } from 'framer-motion';
import { RiSparklingFill } from 'react-icons/ri';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import { useStatusTemplate } from '../../context/StatusTemplateContext';
import type { StatusStep } from '../../context/StatusTemplateContext';
import '../../styles/RenewalSidebarPanel.css';

const StatusIcon: React.FC<{ status: StatusStep['status'] }> = ({ status }) => {
  switch (status) {
    case 'completed':
      return <span className="rsp-icon rsp-icon--completed">✓</span>;
    case 'in_progress':
      return <span className="rsp-icon rsp-icon--progress">⟳</span>;
    case 'failed':
      return <span className="rsp-icon rsp-icon--failed">✗</span>;
    default:
      return <span className="rsp-icon rsp-icon--pending">○</span>;
  }
};

const RenewalSidebarPanel: React.FC = () => {
  const { statusTemplate } = useStatusTemplate();

  const completedCount = statusTemplate?.statusSteps.filter(s => s.status === 'completed').length ?? 0;
  const total = statusTemplate?.statusSteps.length ?? 0;
  const progressPct = total > 0 ? (completedCount / total) * 100 : 0;

  return (
    <SafeAnimatePresence>
      {statusTemplate && (
        <motion.div
          className="rsp"
          initial={{ opacity: 0, x: 24, width: 0 }}
          animate={{ opacity: 1, x: 0, width: 340 }}
          exit={{ opacity: 0, x: 24, width: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="rsp__header">
            <div className="rsp__header-left">
              <RiSparklingFill size={13} className="rsp__header-icon" />
              <span className="rsp__title">{statusTemplate.templateTitle}</span>
            </div>
            <span className="rsp__counter">{completedCount}/{total}</span>
          </div>

          {/* Progress bar */}
          <div className="rsp__progress-track">
            <motion.div
              className="rsp__progress-fill"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>

          {/* Steps */}
          <div className="rsp__steps">
            {statusTemplate.statusSteps.map((step) => (
              <div key={step.step} className={`rsp__step rsp__step--${step.status}`}>
                <span className="rsp__step-num">{step.step}</span>
                <span className="rsp__step-desc">{step.description}</span>
                <StatusIcon status={step.status} />
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </SafeAnimatePresence>
  );
};

export default RenewalSidebarPanel;
